import React from "react";
import { DEFAULT_REACTION_KIND, getReactionPalette, normalizeReactionKind } from "./reactionKinds";

function randomBetween(min, max) {
  return min + Math.random() * (max - min);
}

export function useReactionParticles(options = {}) {
  const {
    particleCount = 12,
    floaterCount = 6,
    dotCount = 12,
    rippleCount = 2,
    minDistance = 36,
    maxDistance = 92,
    minSize = 14,
    maxSize = 24,
    minDuration = 640,
    maxDuration = 1080,
    minDotSize = 5,
    maxDotSize = 9,
    minDotDuration = 380,
    maxDotDuration = 680,
    rippleLifeMs = 560,
  } = options;

  const [particles, setParticles] = React.useState([]);
  const idRef = React.useRef(0);
  const timersRef = React.useRef(new Map());

  const removeParticle = React.useCallback((id) => {
    setParticles((prev) => prev.filter((item) => item.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timerId) => clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  const emitBurst = React.useCallback(
    ({ x, y, emoji = "❤️", kind = DEFAULT_REACTION_KIND, colors = null }) => {
      const safeKind = normalizeReactionKind(kind, DEFAULT_REACTION_KIND);
      const palette =
        Array.isArray(colors) && colors.length
          ? colors
          : getReactionPalette(safeKind);

      const centerX = Math.round(x);
      const centerY = Math.round(y);
      const eventStamp = Date.now();
      let nextId = idRef.current;
      const createId = (prefix) => `${eventStamp}-${prefix}-${++nextId}`;

      const emojiParticles = Array.from({ length: Math.max(6, particleCount) }, () => {
        const angle = randomBetween(-Math.PI * 0.08, Math.PI * 1.08);
        const distance = randomBetween(minDistance, maxDistance);
        const dx = Math.cos(angle) * distance;
        const dy = -Math.abs(Math.sin(angle) * distance) - randomBetween(28, 74);
        return {
          id: createId("emoji"),
          type: "emoji",
          emoji,
          x: centerX + randomBetween(-4, 4),
          y: centerY + randomBetween(-2, 3),
          dx: Math.round(dx),
          dy: Math.round(dy),
          rotate: Math.round(randomBetween(-32, 32)),
          size: Math.round(randomBetween(minSize, maxSize)),
          delayMs: Math.round(randomBetween(0, 110)),
          lifeMs: Math.round(randomBetween(minDuration, maxDuration)),
        };
      });

      const floaters = Array.from({ length: Math.max(4, floaterCount) }, (_, index) => ({
        id: createId("floater"),
        type: "floater",
        emoji,
        x: centerX + randomBetween(-22, 22),
        y: centerY + randomBetween(-3, 8),
        dx: Math.round(randomBetween(-34, 34)),
        dy: Math.round(-randomBetween(54, 96)),
        rotate: Math.round(randomBetween(-18, 18)),
        rotateEnd: Math.round(randomBetween(-42, 42)),
        size: Math.round(randomBetween(Math.max(12, minSize - 2), maxSize + 4)),
        delayMs: Math.round(index * randomBetween(42, 78)),
        lifeMs: Math.round(randomBetween(Math.max(780, minDuration), Math.max(980, maxDuration + 180))),
      }));

      const safeDotCount = Math.max(10, dotCount);
      const dots = Array.from({ length: safeDotCount }, (_, index) => {
        const angle = ((Math.PI * 2 * index) / safeDotCount) + randomBetween(-0.2, 0.2);
        const distance = randomBetween(26, 58);
        return {
          id: createId("dot"),
          type: "dot",
          x: centerX,
          y: centerY,
          dx: Math.round(Math.cos(angle) * distance),
          dy: Math.round(Math.sin(angle) * distance),
          color: palette[index % palette.length],
          size: Math.round(randomBetween(minDotSize, maxDotSize)),
          delayMs: Math.round(index * randomBetween(6, 14)),
          lifeMs: Math.round(randomBetween(minDotDuration, maxDotDuration)),
        };
      });

      const ripples = Array.from({ length: Math.max(1, rippleCount) }, (_, index) => ({
        id: createId("ripple"),
        type: "ripple",
        x: centerX,
        y: centerY,
        size: Math.round(randomBetween(22, 34)),
        color: palette[index % palette.length],
        delayMs: Math.round(index * 90),
        lifeMs: rippleLifeMs,
      }));

      const next = [...ripples, ...dots, ...floaters, ...emojiParticles];
      idRef.current = nextId;

      setParticles((prev) => [...prev, ...next]);
      next.forEach((item) => {
        const timeoutId = setTimeout(() => {
          removeParticle(item.id);
        }, item.lifeMs + item.delayMs + 80);
        timersRef.current.set(item.id, timeoutId);
      });
    },
    [
      dotCount,
      floaterCount,
      maxDistance,
      maxDuration,
      maxDotDuration,
      maxDotSize,
      maxSize,
      minDistance,
      minDuration,
      minDotDuration,
      minDotSize,
      minSize,
      particleCount,
      rippleCount,
      rippleLifeMs,
      removeParticle,
    ]
  );

  const getBurstOriginFromEvent = React.useCallback((event) => {
    const sourceElement =
      event?.currentTarget && typeof event.currentTarget.getBoundingClientRect === "function"
        ? event.currentTarget
        : event?.target && typeof event.target.getBoundingClientRect === "function"
        ? event.target
        : null;
    const sourceRect = sourceElement ? sourceElement.getBoundingClientRect() : null;
    if (sourceRect) {
      return {
        x: sourceRect.left + sourceRect.width / 2,
        y: sourceRect.top + sourceRect.height / 2,
      };
    }
    if (Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)) {
      return { x: event.clientX, y: event.clientY };
    }
    return null;
  }, []);

  const emitBurstFromEvent = React.useCallback(
    (event, { emoji = "❤️", kind = DEFAULT_REACTION_KIND, colors = null } = {}) => {
      const origin = getBurstOriginFromEvent(event);
      if (!origin) return;
      emitBurst({
        x: origin.x,
        y: origin.y,
        emoji,
        kind,
        colors,
      });
    },
    [emitBurst, getBurstOriginFromEvent]
  );

  const clearAllParticles = React.useCallback(() => {
    timersRef.current.forEach((timerId) => clearTimeout(timerId));
    timersRef.current.clear();
    setParticles([]);
  }, []);

  return {
    particles,
    emitBurst,
    getBurstOriginFromEvent,
    emitBurstFromEvent,
    removeParticle,
    clearAllParticles,
  };
}
